import React, { useState, useEffect, useMemo } from "react";
import { Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { styles } from "@components/styles";
import { TriangleUpIcon } from "@components/icons";
import useFilter from "@hooks/useFilter";
import { constants } from "../context/constants";
import { DataScienceContext } from "../context/DataScienceContext";

function SensorList({ project }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [filterTerm, setFilterTerm] = useState("");
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { filterState, filterDispatch, lineColor, activeSeriesColorMap, setActiveInstallations } =
    React.useContext(DataScienceContext);
  const { installationsId, treesId, areasId, installations } = filterState;

  const filteredInstallations = useMemo(
    () => useFilter({ filterTerm, filterItem: installations }),
    [filterTerm, installations]
  );

  useEffect(() => {
    const installationsList = [];
    if (!project?.areas) return;

    project.areas.forEach((area) => {
      area.tree_set
        .filter((tree) => treesId.includes(tree.id))
        .forEach((tree) => {
          // Safety check: ensure installations exists and is an array
          if (tree.installations && Array.isArray(tree.installations)) {
            tree.installations.forEach((installation) => {
              installationsList.push(installation);
            });
          }
        });
    });

    filterDispatch({
      type: constants.RESET_INSTALLATIONS,
      payload: installationsList,
    });
  }, [project, treesId, filterDispatch, areasId]);

  //4. This function used to set the value for tree search on key change to input field.
  function updateFilterHandler(event) {
    setFilterTerm(event.target.value);
  }

  //set and filter hardware serial on check and uncheck
  const handleCheck = (props) => {
    const { e, installation } = props;
    const isChecked = e.target.checked;

    if (isChecked) {
      filterDispatch({
        type: constants.SET_CHECKED_INSTALLATIONS,
        payload: [installation.id],
      });
    } else {
      const filteredInstallation = installationsId.filter((item) => +item !== installation.id);
      filterDispatch({
        type: constants.RESET_CHECKED_INSTALLATIONS,
        payload: filteredInstallation,
      });
    }
  };

  useEffect(() => {
    const allChecked =
      installations.length > 0 &&
      installations.every((installation) => installationsId.includes(installation.id));
    setSelectAllChecked(allChecked);
  }, [installationsId, installations]);

  const handleSelectAll = (props) => {
    const { e, filteredInstallations } = props;
    const isChecked = e.target.checked;

    setSelectAllChecked(isChecked);

    if (isChecked) {
      filterDispatch({
        type: constants.RESET_CHECKED_INSTALLATIONS,
        payload: filteredInstallations.map((installation) => installation.id),
      });
    } else {
      filterDispatch({ type: constants.RESET_CHECKED_INSTALLATIONS, payload: [] });
    }
  };

  useEffect(() => {
    const activeInstallations = installationsId
      .map((i) => {
        const matchedInstallation = installations.find((installation) => installation.id === i);
        if (matchedInstallation) {
          return matchedInstallation;
        }
        return null;
      })
      .filter(Boolean);
    setActiveInstallations(activeInstallations);
  }, [installationsId, installations]);

  return (
    <div className="me-3" data-tour="ds-filter-sensors">
      <div className="d-flex pe-2 justify-content-between">
        <div
          className={styles.dsFilterTitle}
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          aria-expanded={open}
        >
          <TriangleUpIcon open={open} />
          <span className=" ds-filter-title">{t("sensors")} </span>
        </div>
        <div>
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => handleSelectAll({ e, filteredInstallations })}
          />
        </div>
      </div>
      <Collapse in={open}>
        <div>
          {installations.length !== 0 && (
            <div className="py-2 pe-2">
              <input
                type="text"
                className="form-control filter-search"
                placeholder={t("search_sensor")}
                onChange={updateFilterHandler}
              />
            </div>
          )}
          <ul className="p-0 pe-2 filter__list" id="example-collapse-text">
            {filteredInstallations?.map((installation, i) => {
              const isSelected = installationsId.includes(installation.id);
              const color =
                activeSeriesColorMap?.[installation.id] ??
                activeSeriesColorMap?.[String(installation.id)] ??
                lineColor?.[installation.id];
              return (
                <li key={i} className={`filter__items  gap-1 d-flex justify-content-between ps-1`}>
                  {installation.tree.name + " " + installation.sensor.name}
                  <input
                    type="checkbox"
                    onChange={(e) => handleCheck({ e, installation })}
                    style={{
                      background: isSelected
                        ? color || "var(--bs-primary, #0d6efd)"
                        : "var(--color-bg-surface, #ffffff)",
                      border: isSelected
                        ? `2px solid ${color || "var(--bs-primary, #0d6efd)"}`
                        : "1px solid var(--color-border-strong, #888888)",
                      outline: "none",
                      cursor: "pointer",
                    }}
                    checked={isSelected}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </Collapse>
    </div>
  );
}

export default SensorList;
